import { eventChannel, END } from 'redux-saga';

import * as Sentry from '@sentry/browser';

import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';

import { all, put, take, select, takeEvery } from 'redux-saga/effects';

import callsSaga from './calls';
import chainsSaga from './chains';
import dialerSaga from './dialer';
import emailsSaga from './emails';
import errorsSaga, { safeApply, safeCall } from './errors';
import generalSearchSaga from './search';
import logsSaga from './log';
import notificationsSaga from './notifications';
import searchSaga from './searches';
import ticketsSaga from './tickets';
import updatesSaga from './updates';
import voxSaga from './vox';
import statisticSaga from './statistic';
import suggestSaga from './suggest';
import ordersSaga from './orders';
import leadMailer from './leadMailer';
import status from './status';
import tasks from './tasks';
import b2bOrders from './b2bOrders';
import b2bClients from './b2bClients';
import b2bTeam from './b2bTeam';
import b2bLeads from './b2bLeads';
import agentAuth from './agentAuth';

import {
  SENTRY_ENABLED,
  SET_AUTH_TOKEN,
  SET_CURRENT_MANAGER,
  SET_TIME,
  LEADER_INSTANCE_CHANNEL,
  SET_LEADER_INSTANCE,
  UNSET_LEADER_INSTANCE,
  AUTH_REDIRECT_TO_LOG_IN,
  SET_ASSETS_VERSION,
  SET_HAS_ASSET_UPDATES,
  CRM_REQUEST_GENERAL_STATE,
  CRM_SYNC_GENERAL_STATE,
} from '../actions/general';

import {
  VOX_INIT,
  VOX_DESTROY,
  VOX_REQUEST_LAST_STATE,
} from '../../consts/vox';

import { parseCurrentManager } from '../serializers/currentManager';
import fetchData from '../../api/fetchData';
import { isAgentModeSelector } from '../selectors/appMode';

const TIME_SYNCING_INTERVAL = 1 * 60 * 1000;
const CHANGES_CHECKING_INTERVAL = 30 * 60 * 1000;

function* getToken() {
  const isAgentMode = yield select(isAgentModeSelector);

  if (isAgentMode) {
    return null;
  }

  const response = yield safeCall(
    fetchData,
    `${process.env.API_URL}/authorize`
  );
  const token = response?.token || process.env.AUTH_TOKEN || null;

  if (token) {
    yield put({
      payload: { token },
      type: SET_AUTH_TOKEN,
    });
  } else {
    yield put({ type: AUTH_REDIRECT_TO_LOG_IN });
  }

  return token;
}

function* redirectToLogIn() {
  const isAgentMode = yield select(isAgentModeSelector);

  if (isAgentMode || !!process.env.AUTH_TOKEN) return;
  window.location = `${
    process.env.API_HOST_URL
  }/users/login?crm_path=${window.location.pathname.substring(1)}`;
}

function* auth() {
  const isAgentMode = yield select(isAgentModeSelector);

  if (isAgentMode) return;

  const unparsedData = yield safeCall(
    fetchData,
    `${process.env.API_URL}/persons/current`
  );
  const currentManager = yield safeCall(parseCurrentManager, unparsedData);

  if (currentManager) {
    yield put({ payload: { currentManager }, type: SET_CURRENT_MANAGER });

    Sentry.setUser({
      id: currentManager.id,
      username: currentManager.name,
      email: currentManager.email,
    });
  }
}

function createTimeSyncingChannel() {
  return eventChannel((emitter) => {
    const interval = setInterval(() => {
      emitter('Sync!');
    }, TIME_SYNCING_INTERVAL);

    return clearInterval.bind(this, interval);
  });
}

function* syncTime() {
  const { sessionId, currentManager } = yield select((state) => state.general);
  const currentTime = new Date().toString().replace(/\s*\(.*\)$/, '');

  const { time: serverTime } = yield safeCall(
    fetchData,
    `${process.env.API_URL}/meta/time_sync?client_time=${currentTime}&user_id=${currentManager.id}.${sessionId}`
  );

  if (!Number.isInteger(serverTime)) return;

  yield put({ payload: { serverTime }, type: SET_TIME });
}

function* startTimeSyncing() {
  yield safeCall(syncTime);
  const channel = yield safeCall(createTimeSyncingChannel);

  while (true) {
    yield take(channel);
    yield safeCall(syncTime);
  }
}

function createAssetCheckingChannel() {
  return eventChannel((emitter) => {
    const intervalId = setInterval(
      () => emitter('Check!'),
      CHANGES_CHECKING_INTERVAL
    );
    return () => clearInterval(intervalId);
  });
}

function* setAssetVersions() {
  const currentVersions = yield safeCall(
    fetchData,
    `${location.origin}/versions.json`
  );

  if (!currentVersions) return;

  delete currentVersions.success;
  yield put({
    payload: { assetVersions: currentVersions },
    type: SET_ASSETS_VERSION,
  });
}

function* checkAssetUpdates() {
  const latestVersions = yield safeCall(
    fetchData,
    `${location.origin}/versions.json`
  );

  if (!latestVersions) return;

  delete latestVersions.success;

  const pathToCheck = Object.keys(latestVersions);
  const { assetVersions: currentVersions } = yield select(
    (state) => state.general
  );

  const hasAssetUpdates = pathToCheck.some(
    (path) => currentVersions[path] !== latestVersions[path]
  );

  yield put({ payload: { hasAssetUpdates }, type: SET_HAS_ASSET_UPDATES });
}

function* startAssetChecking() {
  yield safeCall(setAssetVersions);
  const channel = yield safeCall(createAssetCheckingChannel);

  while (true) {
    yield take(channel);
    yield safeCall(checkAssetUpdates);
  }
}

function leaderElectionChannel(channel) {
  return eventChannel((emit) => {
    const handleAwaitLeadership = (event) => emit(event);

    const handleDuplicate = () => emit(END);

    // eslint-disable-next-line no-param-reassign
    channel.onduplicate = handleDuplicate;
    channel.awaitLeadership().then(handleAwaitLeadership);

    const unsubscribe = () => channel.die();

    return unsubscribe;
  });
}

function* watchLeaderInstance() {
  const channel = new BroadcastChannel(LEADER_INSTANCE_CHANNEL);
  const leaderElection = yield safeCall(createLeaderElection, channel, {
    fallbackInterval: 5000,
    responseTime: 2000,
  });
  const leaderChannel = yield safeCall(leaderElectionChannel, leaderElection);

  try {
    while (true) {
      yield take(leaderChannel);
      yield put({ type: SET_LEADER_INSTANCE });
      yield put({ type: VOX_INIT });
    }
  } finally {
    yield put({ type: UNSET_LEADER_INSTANCE });
    yield put({ type: VOX_DESTROY });
  }
}

function* syncGeneralState() {
  const { general } = yield select((state) => state);
  const { isMasterInstance } = general;

  if (isMasterInstance) {
    yield put({
      payload: { general },
      type: CRM_SYNC_GENERAL_STATE,
    });
  }
}

function* requestSyncedState() {
  yield put({ type: VOX_REQUEST_LAST_STATE });
  yield put({ type: CRM_REQUEST_GENERAL_STATE });
}

function* initSentry() {
  const isSentryEnabled = process.env.CRM_SENTRY_ENABLED === 'true';

  if (isSentryEnabled) {
    Sentry.init({
      ignoreErrors: [
        'WebSocket:',
        'fetchData',
        'NotFoundError',
        'NotAllowedError',
      ],
      dsn: process.env.CRM_SENTRY_DSN,

      replaysSessionSampleRate: 0,

      replaysOnErrorSampleRate: 1.0,

      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,
          beforeErrorSampling: (error) => error.extra?.eventType === 'message',
        }),
      ],
    });

    yield put({ type: SENTRY_ENABLED });
  }
}

function* init() {
  const isAgentMode = yield select(isAgentModeSelector);

  if (isAgentMode) {
    yield all([
      safeCall(initSentry),
      safeCall(startAssetChecking),
      safeCall(agentAuth),
      safeCall(b2bOrders),
      safeCall(b2bClients),
      safeCall(b2bTeam),
      safeCall(b2bLeads),
    ]);
    return;
  }

  yield all([
    safeCall(initSentry),
    safeCall(requestSyncedState),
    safeCall(startTimeSyncing),
    safeCall(startAssetChecking),
    safeCall(watchLeaderInstance),
    safeCall(callsSaga),
    safeCall(chainsSaga),
    safeCall(dialerSaga),
    safeCall(emailsSaga),
    safeCall(logsSaga),
    safeCall(notificationsSaga),
    safeCall(searchSaga),
    safeCall(ticketsSaga),
    safeCall(updatesSaga),
    safeCall(voxSaga),
    safeCall(statisticSaga),
    safeCall(generalSearchSaga),
    safeCall(suggestSaga),
    safeCall(ordersSaga),
    safeCall(leadMailer),
    safeCall(status),
    safeCall(tasks),
    takeEvery(SET_LEADER_INSTANCE, safeApply(requestSyncedState)),
  ]);
}

export default function* mainSaga() {
  yield all([
    safeCall(getToken),
    safeCall(errorsSaga),
    takeEvery(AUTH_REDIRECT_TO_LOG_IN, safeApply(redirectToLogIn)),
    takeEvery(SET_AUTH_TOKEN, safeApply(auth)),
    takeEvery(SET_CURRENT_MANAGER, safeApply(init)),
    takeEvery(CRM_REQUEST_GENERAL_STATE, safeApply(syncGeneralState)),
  ]);
}
